import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface DiscountCode {
  id: string;
  code: string;
  label: string | null;
  kind: 'PERCENT' | 'FIXED';
  target: 'COMMISSION' | 'PLATFORM_FEE' | 'BROKER_SUBSCRIPTION' | 'ANY';
  value: number | string;
  maxDiscount: number | string | null;
  minOrder: number | string | null;
  userId: string | null;
  tierFilter: string | null;
  maxUsesGlobal: number | null;
  maxUsesPerUser: number | null;
  usedCount: number;
  startsAt: string | null;
  expiresAt: string | null;
  active: boolean;
  createdAt: string;
  _count?: { redemptions: number };
}

const TARGET_LABEL: Record<string, string> = {
  COMMISSION: 'سعي 2.5%',
  PLATFORM_FEE: 'رسوم منصة',
  BROKER_SUBSCRIPTION: 'اشتراك مسوّق',
  ANY: 'أي معاملة',
};

export function AdminDiscountCodesPage() {
  const qc = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'discount-codes'],
    queryFn: () => api.get<ApiSuccess<DiscountCode[]>>('/api/admin/discount-codes'),
  });

  const toggleM = useMutation({
    mutationFn: (id: string) => api.put(`/api/admin/discount-codes/${id}/toggle`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'discount-codes'] }),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/discount-codes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'discount-codes'] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-black text-navy-dark">💸 أكواد الخصم</h1>
        <Button variant="primary" onClick={() => setOpenCreate(true)}>+ كود خصم جديد</Button>
      </div>

      <Card>
        <CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <Th>الكود</Th><Th>النوع</Th><Th>القيمة</Th><Th>الهدف</Th><Th>الاستخدامات</Th><Th>تنتهي</Th><Th>الحالة</Th><Th>إجراءات</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">⟳ جاري التحميل…</td></tr>
              ) : !data?.data?.length ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">لا توجد أكواد</td></tr>
              ) : data.data.map((c) => (
                <tr key={c.id} className="border-b border-gray-100">
                  <td className="px-4 py-3 font-mono font-black text-navy-dark" dir="ltr">
                    {c.code}
                    {c.label && <div className="text-[10px] text-gray-500 font-normal" dir="rtl">{c.label}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge color={c.kind === 'PERCENT' ? 'navy' : 'gold'}>
                      {c.kind === 'PERCENT' ? 'نسبة' : 'مبلغ'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 font-bold">
                    {Number(c.value)}{c.kind === 'PERCENT' ? '%' : ' ر.س'}
                  </td>
                  <td className="px-4 py-3 text-xs">{TARGET_LABEL[c.target]}</td>
                  <td className="px-4 py-3 text-sm">
                    {c.usedCount}{c.maxUsesGlobal ? `/${c.maxUsesGlobal}` : ''}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString('ar-SA') : 'دائم'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge color={c.active ? 'green' : 'red'}>{c.active ? 'نشط' : 'موقوف'}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => toggleM.mutate(c.id)}>{c.active ? '⏸' : '▶'}</Button>
                      <Button size="sm" variant="ghost" onClick={() => { if (confirm(`حذف ${c.code}؟`)) deleteM.mutate(c.id); }}>🗑</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {openCreate && <CreateDiscountModal onClose={() => setOpenCreate(false)} onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'discount-codes'] })} />}
    </div>
  );
}

function CreateDiscountModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [f, setF] = useState({
    code: '', label: '',
    kind: 'PERCENT' as 'PERCENT' | 'FIXED',
    target: 'ANY',
    value: '', maxDiscount: '', minOrder: '',
    userId: '', tierFilter: 'ANY',
    maxUsesGlobal: '', maxUsesPerUser: '',
    expiresInDays: '',
  });
  const [err, setErr] = useState('');

  const m = useMutation({
    mutationFn: () => api.post('/api/admin/discount-codes', {
      code: f.code,
      label: f.label || undefined,
      kind: f.kind,
      target: f.target,
      value: Number(f.value),
      maxDiscount: f.maxDiscount ? Number(f.maxDiscount) : undefined,
      minOrder: f.minOrder ? Number(f.minOrder) : undefined,
      userId: f.userId || undefined,
      tierFilter: f.tierFilter === 'ANY' ? undefined : f.tierFilter,
      maxUsesGlobal: f.maxUsesGlobal ? Number(f.maxUsesGlobal) : undefined,
      maxUsesPerUser: f.maxUsesPerUser ? Number(f.maxUsesPerUser) : undefined,
      expiresInDays: f.expiresInDays ? Number(f.expiresInDays) : undefined,
    }),
    onSuccess: () => { onCreated(); onClose(); },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">💸 كود خصم جديد</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="الكود *" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} dir="ltr" placeholder="SUMMER25" />
            <Input label="وصف داخلي" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />

            <div>
              <label className="block text-sm font-bold text-ink mb-1">نوع الخصم *</label>
              <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as never })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
                <option value="PERCENT">نسبة مئوية %</option>
                <option value="FIXED">مبلغ ثابت ر.س</option>
              </select>
            </div>
            <Input
              label={`القيمة * ${f.kind === 'PERCENT' ? '(0-100)' : '(ر.س)'}`}
              type="number"
              value={f.value}
              onChange={(e) => setF({ ...f, value: e.target.value })}
            />

            <div>
              <label className="block text-sm font-bold text-ink mb-1">يطبّق على</label>
              <select value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
                <option value="ANY">أي معاملة</option>
                <option value="COMMISSION">سعي 2.5%</option>
                <option value="PLATFORM_FEE">رسوم المنصة</option>
                <option value="BROKER_SUBSCRIPTION">اشتراك المسوّق</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-1">مستوى محدّد (اختياري)</label>
              <select value={f.tierFilter} onChange={(e) => setF({ ...f, tierFilter: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white">
                <option value="ANY">للجميع</option>
                <option value="GREEN">أخضر فقط</option>
                <option value="GOLD">ذهبي فقط</option>
                <option value="DIAMOND">ماسي فقط</option>
              </select>
            </div>

            <Input label="الحد الأدنى للمعاملة (ر.س)" type="number" value={f.minOrder} onChange={(e) => setF({ ...f, minOrder: e.target.value })} placeholder="اختياري" />
            {f.kind === 'PERCENT' && (
              <Input label="سقف الخصم (ر.س)" type="number" value={f.maxDiscount} onChange={(e) => setF({ ...f, maxDiscount: e.target.value })} placeholder="اختياري" />
            )}

            <Input label="مستخدم محدّد (ID)" value={f.userId} onChange={(e) => setF({ ...f, userId: e.target.value })} dir="ltr" placeholder="فارغ = للجميع" />
            <Input label="ينتهي خلال (يوم)" type="number" value={f.expiresInDays} onChange={(e) => setF({ ...f, expiresInDays: e.target.value })} placeholder="فارغ = دائم" />
            <Input label="حد استخدامات عام" type="number" value={f.maxUsesGlobal} onChange={(e) => setF({ ...f, maxUsesGlobal: e.target.value })} placeholder="فارغ = غير محدود" />
            <Input label="حد استخدامات للمستخدم" type="number" value={f.maxUsesPerUser} onChange={(e) => setF({ ...f, maxUsesPerUser: e.target.value })} placeholder="فارغ = غير محدود" />
          </div>
          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">⛔ {err}</div>}
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="ghost" onClick={onClose}>إلغاء</Button>
            <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.code || !f.value}>إنشاء</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-right px-4 py-3 font-bold text-gray-600 text-xs uppercase">{children}</th>;
}
